import { useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { ChevronLeft, ChevronRight, Download, HandCoins } from "lucide-react";

// Lot B (10/2026) — grand livre des commissions freelances, vue SUPER_ADMIN :
// relevé par freelance, MARQUAGE PAYÉ (référence de virement obligatoire) et
// export CSV — deux gestes réservés à l'app Admin (ADMIN_SALES valide dans
// l'OPS, les freelances n'exportent rien). Tout est audité.
// Endpoints : admin.commissions.listEntries / statement / markPaid / exportCsv.

type Status = "PENDING" | "VALIDATED" | "PAID" | "CANCELLED";

const STATUS: Record<Status, { label: string; variant: "default" | "secondary" | "outline" | "warning" }> = {
  PENDING: { label: "En attente", variant: "outline" },
  VALIDATED: { label: "Validée", variant: "warning" },
  PAID: { label: "Payée", variant: "default" },
  CANCELLED: { label: "Annulée", variant: "secondary" },
};
const KIND: Record<string, string> = {
  HONORED_MEETING: "RDV tenu",
  CONVERSION: "Prime de conversion",
  COLLECTED_REVENUE: "% encaissé",
  ADJUSTMENT: "Ajustement",
};

const eur = (cents: number) => (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const thisMonth = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" }).slice(0, 7);
const name = (u: { firstName: string | null; lastName: string | null; email: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;

export default function Commissions() {
  const utils = trpc.useUtils();
  const [month, setMonth] = useState(thisMonth());
  const [status, setStatus] = useState<Status | "">("VALIDATED");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reference, setReference] = useState("");

  const filters = { periodMonth: month || undefined, status: status || undefined };
  const entries = trpc.admin.commissions.listEntries.useQuery({ ...filters, page, limit: 50 });
  const statement = trpc.admin.commissions.statement.useQuery({ periodMonth: month || undefined });

  const markPaid = trpc.admin.commissions.markPaid.useMutation({
    onSuccess: (r) => {
      toast.success(`${r.paid} écriture(s) marquée(s) payée(s)` + (r.skipped ? ` — ${r.skipped} ignorée(s) (non validées).` : "."));
      setSelected(new Set());
      setReference("");
      void utils.admin.commissions.listEntries.invalidate();
      void utils.admin.commissions.statement.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const exportCsv = trpc.admin.commissions.exportCsv.useMutation({
    onSuccess: (data) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e) => toast.error(e.message),
  });

  // Revue #337 (L4) : back-fill d'une opportunité rattachée tardivement.
  const [bfLead, setBfLead] = useState("");
  const [bfReason, setBfReason] = useState("");
  const backfill = trpc.admin.commissions.backfillLead.useMutation({
    onSuccess: (r) => {
      toast.success(
        r.mode === "STRIPE" ? `${r.processed} facture(s) re-traitée(s).` :
        r.mode === "JOURNAL_ONLY" ? "Conversion retracée (Stripe indisponible : commissions à ajuster)." :
        "Rien à traiter (attribution non confirmée)."
      );
      setBfLead("");
      setBfReason("");
      void utils.admin.commissions.listEntries.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const items = entries.data?.items ?? [];
  const payable = items.filter((e) => e.status === "VALIDATED").map((e) => e.id);
  const selectCls = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HandCoins className="h-6 w-6" /> Commissions freelances
            </h1>
            <p className="text-sm text-muted-foreground">
              Les écritures sont validées par le manager commercial (app OPS), puis payées ici après virement.
            </p>
          </div>
          <Button variant="outline" disabled={exportCsv.isLoading} onClick={() => exportCsv.mutate(filters)}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Input type="month" className="w-44" value={month} onChange={(e) => { setMonth(e.target.value); setPage(1); }} />
          <Button variant="outline" size="sm" onClick={() => { setMonth(""); setPage(1); }}>Toutes périodes</Button>
          <select className={selectCls} value={status} onChange={(e) => { setStatus(e.target.value as Status | ""); setPage(1); }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUS) as Status[]).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
          </select>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Relevé par freelance</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Freelance</TableHead>
                  <TableHead className="text-right">En attente</TableHead>
                  <TableHead className="text-right">Validé (à payer)</TableHead>
                  <TableHead className="text-right">Payé</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(statement.data ?? []).map((s) => (
                  <TableRow key={s.userId}>
                    <TableCell>
                      {s.name} <span className="text-xs text-muted-foreground">({s.role})</span>
                      {s.rateMissing > 0 && <div className="text-xs text-amber-600">{s.rateMissing} écriture(s) sans barème</div>}
                    </TableCell>
                    <TableCell className="text-right">{eur(s.pending)}</TableCell>
                    <TableCell className="text-right font-medium">{eur(s.validated)}</TableCell>
                    <TableCell className="text-right">{eur(s.paid)}</TableCell>
                  </TableRow>
                ))}
                {statement.data && statement.data.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">Aucune écriture sur la période.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Back-fill d'une opportunité</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2 items-center">
            <Input className="w-72" placeholder="Identifiant de l'opportunité (Lead)" value={bfLead} onChange={(e) => setBfLead(e.target.value)} />
            <Input className="w-72" placeholder="Motif (obligatoire)" value={bfReason} onChange={(e) => setBfReason(e.target.value)} />
            <Button
              variant="outline"
              disabled={!bfLead.trim() || bfReason.trim().length < 3 || backfill.isLoading}
              onClick={() => backfill.mutate({ leadId: bfLead.trim(), reason: bfReason.trim() })}
            >
              Re-traiter les factures payées
            </Button>
            <p className="text-xs text-muted-foreground w-full">
              Pour un rattachement tardif ou une égalité tranchée à la main : rejoue les factures déjà payées (idempotent, audité).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Écritures</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                className="w-64"
                placeholder="Référence du virement"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <Button
                disabled={selected.size === 0 || reference.trim().length < 2 || markPaid.isLoading}
                onClick={() => markPaid.mutate({ ids: [...selected], reference: reference.trim() })}
              >
                Marquer payées ({selected.size})
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Tout sélectionner"
                        checked={payable.length > 0 && payable.every((id) => selected.has(id))}
                        onChange={(e) => setSelected(e.target.checked ? new Set(payable) : new Set())}
                      />
                    </TableHead>
                    <TableHead>Mois</TableHead>
                    <TableHead>Freelance</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Objet</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>
                        {e.status === "VALIDATED" && (
                          <input
                            type="checkbox"
                            aria-label="Sélectionner"
                            checked={selected.has(e.id)}
                            onChange={(ev) => {
                              const next = new Set(selected);
                              if (ev.target.checked) next.add(e.id);
                              else next.delete(e.id);
                              setSelected(next);
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{e.periodMonth}</TableCell>
                      <TableCell>{name(e.User)}</TableCell>
                      <TableCell>{KIND[e.kind] ?? e.kind}</TableCell>
                      <TableCell className="text-sm">
                        {e.Meeting?.AcquisitionCompany?.name ?? e.Lead?.company ?? "—"}
                        {e.rateMissing && <div className="text-xs text-amber-600">Barème non défini</div>}
                        {e.paymentReference && <div className="text-xs text-muted-foreground">Virement {e.paymentReference}</div>}
                        {e.notes && !e.rateMissing && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                        {e.selfValidated && <div className="text-xs text-amber-600 font-medium">⚠ Créée et validée par la même personne</div>}
                        {e.status === "PENDING" && e.holdUntil && new Date(e.holdUntil) > new Date() && (
                          <div className="text-xs text-sky-700">En période de garantie jusqu'au {new Date(e.holdUntil).toLocaleDateString("fr-FR")}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS[e.status as Status]?.variant ?? "outline"}>
                          {STATUS[e.status as Status]?.label ?? e.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">{eur(e.amountCents)}</TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground">Aucune écriture.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {(entries.data?.totalPages ?? 1) > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Page précédente">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>{page} / {entries.data?.totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= (entries.data?.totalPages ?? 1)} onClick={() => setPage(page + 1)} aria-label="Page suivante">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
